import React from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/use-profile';
import { useMatureContent } from '@/hooks/use-mature-content';
import { useLazyImage } from '@/hooks/use-lazy-image';
import BulletinDeleteButton from '@/components/bulletins/BulletinDeleteButton';
import PostUploadProgress from '@/components/bulletins/PostUploadProgress';

export interface TestBulletinItem {
  id: string;
  user_id: string;
  image_url: string | null;
  thumbnail_url: string | null;
  is_relic: boolean | null;
  is_mature?: boolean | null;
  created_at: string;
  like_count?: number | null;
  // Optional fields used by inspect UI and feed
  title?: string | null;
  content?: string | null;
  content_labels?: string[] | null;
  file_type?: string | null;
  image_version?: number | null;
  saved_at?: string | null;
  expires_at?: string | null;
  auto_category?: string | null;
  user_category?: string | null;
}

interface CardProps {
  item: TestBulletinItem;
  liked: boolean;
  onLike: (id: string) => Promise<void>;
  onOpen: (item: TestBulletinItem) => void;
  onDelete?: (id: string) => Promise<void>;
  isLarge?: boolean;
  uploadProgress?: { progress: number; state: string };
}

const TestBulletinCard: React.FC<CardProps> = ({ item, liked, onLike, onOpen, onDelete, isLarge, uploadProgress }) => {
  const { user } = useAuth();
  const { profile: authorProfile } = useProfile(item.user_id);
  const { profile: viewerProfile } = useProfile(user?.id);
  const { showMatureContent } = useMatureContent();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [animKey, setAnimKey] = React.useState(0);
  const [showBurst, setShowBurst] = React.useState(false);
  const [showAura, setShowAura] = React.useState(false);

  const canDelete = !!user && (user.id === item.user_id || !!viewerProfile?.is_admin);

  const imgSrc = item.thumbnail_url || item.image_url || '';
  const { currentSrc, imgRef, blur: isLazyBlur } = useLazyImage(imgSrc, { placeholderSrc: '/placeholder.svg' });
  const shouldBlurMature = !!item.is_mature && !showMatureContent;
  const displayLikeCount = (item.like_count ?? 0) + (liked ? 1 : 0);

  return (
    <article
      className={`break-inside-avoid overflow-hidden rounded-xl bg-card shadow-sm transition-transform duration-200 ease-[cubic-bezier(.2,.8,.2,1)] hover:shadow-lg hover:-translate-y-0.5 mb-2 md:mb-3 ${isLarge ? 'col-span-2 row-span-2' : ''}`}
    >
      <div className="relative group cursor-pointer h-full" onClick={() => onOpen(item)}>
        {/* Admin/User delete control - always available when permitted, but not for pending posts */}
        {canDelete && !item.id.startsWith('pending-') && (
          <div className="absolute top-0 right-0 mt-2 mr-2 z-10" onClick={(e) => e.stopPropagation()}>
            <BulletinDeleteButton
              bulletinId={item.id}
              onDelete={() => onDelete?.(item.id)}
              isDeleting={isDeleting}
              setIsDeleting={setIsDeleting}
            />
          </div>
        )}
        <img
          ref={imgRef}
          src={currentSrc || '/placeholder.svg'}
          alt="Bulletin media"
          className={`w-full h-auto object-contain ${shouldBlurMature ? 'blur-xl' : ''} ${isLazyBlur ? 'blur-sm' : ''}`}
          loading="lazy"
        />

        {item.id.startsWith('pending-') && uploadProgress && (
          <PostUploadProgress progress={uploadProgress.progress} state={uploadProgress.state} />
        )}

        {/* Overlay actions */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        {/* Like button (bottom-right) */}
        <div className="absolute bottom-2 right-2 flex items-center justify-end space-x-1">
          <Button
            size="sm"
            variant="ghost"
            className={`px-2 py-1 hover:bg-transparent ${liked ? 'text-red-500' : 'text-foreground'}`}
            onClick={(e) => {
              e.stopPropagation();
              // Trigger particle burst + aura crown
              setAnimKey((k) => k + 1);
              setShowBurst(true);
              setShowAura(true);
              window.setTimeout(() => setShowBurst(false), 600);
              window.setTimeout(() => setShowAura(false), 800);
              // Persist like
              onLike(item.id);
            }}
            aria-label="Like"
            aria-pressed={liked}
          >
            <Heart className={`w-4 h-4 transition-opacity filter drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] ${liked ? 'fill-current' : 'opacity-50'}`} />
            {liked && (
              <span
                className="text-xs ml-1 text-white"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.85)' }}
              >
                {displayLikeCount}
              </span>
            )}
          </Button>
          {/* Relic badge removed: relics are sunset */}
        </div>

        {/* Particle burst on like */}
        {showBurst && (
          <div key={animKey} className="pointer-events-none absolute inset-0 overflow-hidden">
            {Array.from({ length: 10 }).map((_, i) => (
              <span
                key={i}
                className="absolute block h-1 w-1 rounded-full bg-primary/80"
                style={{
                  left: 'calc(100% - 20px)',
                  top: 'calc(100% - 20px)',
                  transformOrigin: 'center',
                  animation: `fomo-pop 600ms ease-out forwards`,
                  // distribute angles and speeds
                  ['--tx' as any]: `${Math.cos((i / 10) * Math.PI * 2) * 70}px`,
                  ['--ty' as any]: `${Math.sin((i / 10) * Math.PI * 2) * 70}px`,
                } as React.CSSProperties}
              />
            ))}
          </div>
        )}

        {/* Crown/Aura animation near top-center */}
        {showAura && (
          <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-3">
            <svg width="32" height="32" viewBox="0 0 24 24" className="text-yellow-400" style={{ animation: 'fomo-aura 800ms ease-out forwards' }}>
              <path fill="currentColor" d="M5 19h14l-1.5-9-3.5 3-3-6-3 6-3.5-3L5 19z" />
            </svg>
          </div>
        )}

        {/* Author info - hidden until liked */}
        {liked && (
          authorProfile ? (
            <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-full overflow-hidden ring-2 ring-offset-2 ring-offset-background ${authorProfile?.is_admin ? 'ring-accent' : 'ring-primary'}`}>
                  {authorProfile?.profile_image_url ? (
                    <img src={authorProfile.profile_image_url} alt="Author avatar" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full bg-muted" />
                  )}
                </div>
                <div className="text-xs font-medium truncate max-w-[100px] sm:max-w-[140px]">
                  {authorProfile.display_name || authorProfile.username}
                </div>
              </div>
            </div>
          ) : (
            <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
                <div className="h-3 w-24 rounded bg-muted animate-pulse" />
              </div>
            </div>
          )
        )}

        {item.is_mature && !showMatureContent && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="px-3 py-2 rounded-md bg-background/80 backdrop-blur text-xs border border-border">
              Mature content – enable in settings to view
            </div>
          </div>
        )}
      </div>
      {/* Scoped animation keyframes */}
      <style>
        {`
        @keyframes fomo-pop {
          0% { opacity: 0; transform: translate(0,0) scale(0.6); }
          10% { opacity: 1; }
          100% { opacity: 0; transform: translate(var(--tx, 0), var(--ty, 0)) scale(1); }
        }
        @keyframes fomo-aura {
          0% { opacity: 0; transform: translateY(6px) scale(0.8); filter: drop-shadow(0 0 0 rgba(250, 204, 21, 0)); }
          40% { opacity: 1; transform: translateY(0) scale(1); filter: drop-shadow(0 4px 10px rgba(250, 204, 21, 0.8)); }
          100% { opacity: 0; transform: translateY(-6px) scale(0.9); filter: drop-shadow(0 0 0 rgba(250, 204, 21, 0)); }
        }
        `}
      </style>
    </article>
  );
};

export default TestBulletinCard;
